import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

interface VideoPlayerProps {
  videoId: string;
  onReady?: (player: YT.Player) => void;
}

export default function VideoPlayer({ videoId, onReady }: VideoPlayerProps) {
  const playerRef = useRef<HTMLDivElement>(null);
  const ytPlayerRef = useRef<YT.Player | null>(null);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    const initPlayer = () => {
      if (!playerRef.current || !window.YT?.Player) return;

      ytPlayerRef.current = new window.YT.Player(playerRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          mute: 1,           // Required for autoplay on iOS Safari
          modestbranding: 1,
          rel: 0,
          fs: 1,
          iv_load_policy: 3,
          cc_load_policy: 0,
          enablejsapi: 1,
          playsinline: 0,    // 0 = use native fullscreen player on iOS
        },
        events: {
          onReady: (event) => {
            const player = event.target;
            player.unMute();
            player.playVideo();
            onReady?.(player);
          },
        },
      });
    };

    if (window.YT?.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (ytPlayerRef.current) {
        ytPlayerRef.current.destroy();
      }
    };
  }, [videoId, onReady]);

  return (
    <Box
      sx={{
        position: 'relative',
        paddingTop: '56.25%',
        width: '100%',
        backgroundColor: 'black',
      }}
    >
      <Box
        ref={playerRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  );
}

// Extend Window interface for YouTube API
declare global {
  interface Window {
    YT: typeof YT;
    onYouTubeIframeAPIReady: () => void;
  }

  namespace YT {
    class Player {
      constructor(element: HTMLElement, config: PlayerConfig);
      destroy(): void;
      getCurrentTime(): number;
      getDuration(): number;
      playVideo(): void;
      pauseVideo(): void;
      unMute(): void;
    }

    interface PlayerConfig {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: PlayerVars;
      events?: PlayerEvents;
    }

    interface PlayerVars {
      autoplay?: 0 | 1;
      modestbranding?: 0 | 1;
      rel?: 0 | 1;
      [key: string]: unknown;
    }

    interface PlayerEvents {
      onReady?: (event: PlayerEvent) => void;
      onStateChange?: (event: PlayerEvent) => void;
    }

    interface PlayerEvent {
      target: Player;
      data?: number;
    }
  }
}
